import { DialogComponent } from '@theme/dialog';
import { fetchConfig, startViewTransition } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Cart popup — receipt-style overlay (sections/apc-cart.liquid).
 *
 * Opens from the shop section's cart link (a.apc-shop__cart) or any element
 * with [data-apc-cart-open]. Item removal posts to the Cart API and swaps the
 * panel with freshly server-rendered section HTML.
 *
 * @typedef {object} ApcCartRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} panel - The receipt panel (replaced on re-render).
 */
class ApcCartPopup extends DialogComponent {
  requiredRefs = ['dialog', 'panel'];

  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#onTriggerClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    // /cart redirects here with ?cart=open (sections/apc-cart-redirect.liquid).
    if (new URLSearchParams(window.location.search).get('cart') === 'open') {
      this.showDialog();
      const url = new URL(window.location.href);
      url.searchParams.delete('cart');
      history.replaceState({}, '', url);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onTriggerClick);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    // Our own removeItem requests the section in the change response; anything
    // else (e.g. the shop's add) needs a fresh server render.
    const html = event.detail?.data?.sections?.[this.dataset.sectionId ?? ''];
    if (html) this.#morphPanel(html);
    else this.#rerender().catch(console.error);
  };

  /** @param {MouseEvent} event */
  #onTriggerClick = (event) => {
    // Leave modifier clicks alone (open in new tab etc.).
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = /** @type {Element} */ (event.target);
    if (!target.closest('a.apc-shop__cart, [data-apc-cart-open]')) return;

    event.preventDefault();
    this.showDialog();
  };

  /**
   * Removes a line item, then re-renders the popup from the server.
   * @param {MouseEvent} event
   */
  async removeItem(event) {
    const button = /** @type {HTMLButtonElement | null} */ (
      /** @type {Element} */ (event.target).closest('[data-line-key]')
    );
    if (!button || this.#busy) return;

    this.#busy = true;
    button.disabled = true;

    try {
      const response = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          // Ask for our section in the same response — no second round trip.
          body: JSON.stringify({ id: button.dataset.lineKey, quantity: 0, sections: this.dataset.sectionId }),
        })
      );
      if (!response.ok) throw new Error(`Cart change failed: ${response.status}`);
      const cart = await response.json();
      // The cart:update listener (this component included) does the re-render.
      document.dispatchEvent(
        new CartUpdateEvent(cart, this.dataset.sectionId, {
          source: 'apc-cart',
          itemCount: cart.item_count,
          sections: cart.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#busy = false;
      // Morph replaces the line on success; on failure this re-enables retry.
      button.disabled = false;
    }
  }

  async #rerender() {
    const response = await fetch(`${window.location.pathname}?section_id=${this.dataset.sectionId}`);
    if (!response.ok) throw new Error(`Cart section render failed: ${response.status}`);
    this.#morphPanel(await response.text());
  }

  /** @param {string} html - Server-rendered section HTML. */
  #morphPanel(html) {
    const panel = new DOMParser().parseFromString(html, 'text/html').querySelector('.apc-cart__panel');
    if (!panel) return;

    // Morph (same approach as the main cart's morphSection) so unchanged
    // nodes stay in place, wrapped in a view transition so the removed line
    // fades out and the lines below glide up (instant when unsupported).
    // Panel only: the fetched dialog has no [open], a full morph would close it.
    startViewTransition(() => morph(this.refs.panel, panel));
  }
}

if (!customElements.get('apc-cart-popup')) customElements.define('apc-cart-popup', ApcCartPopup);
